import type { NotificationView } from '@chukua/common';
import clsx from 'clsx';
import { BellOff } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../shared/components/RowListSkeleton';
import type { NotificationListState } from '../hooks/use-notifications';

interface NotificationListProps {
  state: NotificationListState;
  onSelect: (notification: NotificationView) => void;
}

/** The bell's popover body — unread first (the API's order), each row a button so it works from the keyboard. */
export function NotificationList({ state, onSelect }: NotificationListProps) {
  if (state.status === 'idle' || state.status === 'loading') return <RowListSkeleton label="Loading notifications" rows={3} />;
  if (state.status === 'error') return <p className="m-0 p-2 text-sm text-muted">Couldn&apos;t load notifications. Close this and try again.</p>;
  if (state.items.length === 0) return <EmptyState icon={BellOff} title="You're all caught up" description="Nothing needs your attention right now." />;

  return (
    <ul className="m-0 flex max-h-96 w-80 max-w-full list-none flex-col gap-1 overflow-y-auto p-0">
      {state.items.map((notification) => (
        <li key={notification.id}>
          <button
            type="button"
            onClick={() => onSelect(notification)}
            className={clsx(
              'flex w-full flex-col gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sider-selected',
              !notification.readAt && 'bg-primary/10',
            )}
          >
            <span className={clsx('text-[13px] text-fg', !notification.readAt && 'font-semibold')}>{notification.title}</span>
            <span className="text-xs text-muted">{notification.body}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
